import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { KEY_CODES, KEY_DOWN } from '@outdoor-voices/ui/dist/config/constants';
import { lockScroll, unlockScroll } from '@outdoor-voices/ui/dist/utils/dom/scroll';
import cc from 'classcat';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useRef, useState } from 'react';
import css from './Overlay.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var elementToReturnFocusTo = null;

var Overlay = function Overlay(_ref) {
  var ariaLabelledBy = _ref.ariaLabelledBy,
      ariaDescribedBy = _ref.ariaDescribedBy,
      children = _ref.children,
      label = _ref.label,
      on = _ref.on,
      onHide = _ref.onHide,
      onShow = _ref.onShow,
      query = _ref.query;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      locked = _useState2[0],
      setLocked = _useState2[1]; // const [mounted, setMounted] = useState(false);


  var describedBy = ariaDescribedBy ? {
    'aria-describedby': ariaDescribedBy
  } : null;
  var labelledBy = ariaLabelledBy ? {
    'aria-labelledby': ariaLabelledBy
  } : null;

  var accessibilities = _objectSpread(_objectSpread({}, describedBy), labelledBy);

  var rootEl = useRef(null);

  if (on) {
    // Track the currently focused element so we can return to it on close.
    elementToReturnFocusTo = document.activeElement;
  } // Handle scroll locking


  useEffect(function () {
    if (on && locked) {
      return;
    }

    if (on && !locked) {
      setLocked(true);
      lockScroll();
      return;
    }

    if (!locked) {
      return;
    }

    unlockScroll();
    setLocked(false);
  }, [locked, on]);
  useEffect(function () {
    var dialog = rootEl.current;

    if (!dialog || !on) {
      return;
    }

    var onKeyDown = function onKeyDown(event) {
      // add all the elements which you want to make focusable
      var focusableContent = dialog.querySelectorAll(query);
      var first = focusableContent[0];
      var last = focusableContent[focusableContent.length - 1];
      var isTabPressed = event.key === KEY_CODES.TAB;

      if (event.key === KEY_CODES.ESC) {
        onHide(event);
        return;
      }

      if (!isTabPressed) {
        return;
      }

      var activeElementIndex = _toConsumableArray(focusableContent).indexOf(document.activeElement);

      if (event.shiftKey) {
        // if shift key pressed for shift + tab combination
        if (document.activeElement === first || activeElementIndex < 0) {
          last.focus(); // add focus for the last focusable element

          event.preventDefault();
        }
      } else {
        // if tab key is pressed
        // eslint-disable-next-line no-lonely-if
        if (document.activeElement === last || activeElementIndex < 0) {
          // if focused has reached to last focusable element then focus first focusable element after pressing tab
          first.focus();
          event.preventDefault();
        }
      }
    };

    document.removeEventListener(KEY_DOWN, onKeyDown);
    document.addEventListener(KEY_DOWN, onKeyDown);
    return function () {
      document.removeEventListener(KEY_DOWN, onKeyDown);
    };
  }, [on, onHide, query]);
  useEffect(function () {
    var dialog = rootEl.current;

    if (!dialog || !on) {
      return;
    }

    var timeout = setTimeout(function () {
      var focusableContent = rootEl.current.querySelectorAll(query);
      var first = focusableContent[0];

      if (first) {
        first.focus();
      }

      onShow();
    }, 200); // eslint-disable-next-line consistent-return

    return function () {
      clearTimeout(timeout); // Refocus the element on the original focused element

      if (elementToReturnFocusTo) {
        elementToReturnFocusTo.focus();
      }
    };
  }, [on, onShow, query]);
  useEffect(function () {
    return function () {
      unlockScroll();
    };
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({}, accessibilities), {}, {
      "aria-label": "".concat(label, " dialog"),
      "aria-modal": "true",
      ref: rootEl,
      role: "dialog",
      children: children
    })), /*#__PURE__*/_jsx("button", {
      className: cc([css.bg, _defineProperty({}, css.on, on)]),
      onClick: onHide,
      "aria-label": "Close dialog",
      title: "Close dialog",
      type: "button",
      tabIndex: -1
    })]
  });
};

Overlay.defaultProps = {
  ariaDescribedBy: null,
  ariaLabelledBy: null,
  label: 'Modal',
  on: false,
  query: 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  onShow: function onShow() {}
};
Overlay.propTypes = {
  ariaDescribedBy: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  ariaLabelledBy: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  on: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onShow: PropTypes.func,
  label: PropTypes.string,
  query: PropTypes.string
};
export default /*#__PURE__*/memo(Overlay);